import React, { useEffect, useState } from "react";
import api from "./api";
import { Card, BackButton, Button } from "./ui";
import ActivityChat from "./ActivityChat";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function calendarLinks(a) {
  const pad = (n) => String(n).padStart(2, "0");
  const start = new Date(`${a.date}T${a.time || "09:00"}:00`);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const fmt = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
  const range = `${fmt(start)}/${fmt(end)}`;
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  const details = [a.short_description, a.requirements ? `Requisitos: ${a.requirements}` : "", a.organizer_name ? `Organiza: ${a.organizer_name}` : "", "Vía PawCita 🐾"].filter(Boolean).join(" — ");
  const loc = [a.location, a.city].filter(Boolean).join(", ");
  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(a.title)}&dates=${range}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(loc)}`;
  const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(a.title)}&startdt=${encodeURIComponent(iso(start))}&enddt=${encodeURIComponent(iso(end))}&body=${encodeURIComponent(details)}&location=${encodeURIComponent(loc)}`;
  const ics = `${BACKEND}/api/activities/${a.id}/calendar.ics`;
  return { google, outlook, ics };
}

export const ACTIVITY_CATS = [
  { key: "pet_date", label: "Pet date", emoji: "🐾" },
  { key: "caminata", label: "Caminata", emoji: "🚶" },
  { key: "entrenamiento", label: "Entrenamiento", emoji: "🎓" },
  { key: "salud", label: "Salud / veterinaria", emoji: "🏥" },
  { key: "grooming", label: "Grooming", emoji: "✂️" },
  { key: "taller", label: "Taller", emoji: "🎨" },
  { key: "comunidad", label: "Comunidad", emoji: "🏡" },
  { key: "adopcion", label: "Adopción", emoji: "🐶" },
  { key: "evento_especial", label: "Evento especial", emoji: "🎉" },
];

export const actCat = (k) => ACTIVITY_CATS.find((c) => c.key === k) || { label: k, emoji: "🐾" };

function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(`${d}T00:00:00`);
    return dt.toLocaleDateString("es", { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return d;
  }
}

export function Detail({ activity, onBack }) {
  const info = actCat(activity.category);
  const [act, setAct] = useState(activity);
  const [part, setPart] = useState(null); // {joined, dog_name}
  const [dogs, setDogs] = useState(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const refresh = () => {
    api.activities.detail(activity.id).then(setAct).catch(() => {});
    api.activities.myParticipation(activity.id).then(setPart).catch(() => setPart({ joined: false }));
  };
  useEffect(refresh, [activity.id]);

  const startJoin = () => {
    setErr("");
    setPicking(true);
    if (dogs === null) {
      api.dogs.list().then(setDogs).catch(() => setDogs([]));
    }
  };

  const doJoin = async (dogId) => {
    setBusy(true);
    setErr("");
    try {
      await api.activities.join(activity.id, dogId);
      setPicking(false);
      refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doLeave = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.activities.leave(activity.id);
      refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cap = act.capacity_num;
  const count = act.participants_count || 0;
  const full = cap != null && count >= cap;
  const [calOpen, setCalOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const cal = calendarLinks(act);

  if (chatOpen) {
    return (
      <div className="h-[calc(100vh-9rem)]">
        <ActivityChat activityId={act.id} title={act.title} onBack={() => setChatOpen(false)} />
      </div>
    );
  }

  return (
    <div data-testid="activity-detail" className="pb-32">
      <BackButton onClick={onBack} label="Volver a actividades" testId="activity-detail-back" />
      {act.image ? (
        <img src={act.image} alt={act.title} className="mt-3 h-52 w-full rounded-3xl object-cover" data-testid="activity-detail-image" />
      ) : (
        <div className="mt-3 flex h-44 w-full items-center justify-center rounded-3xl bg-gradient-to-br from-[#67D5B5]/25 to-[#0B2D4D]/10 text-6xl">
          {info.emoji}
        </div>
      )}
      <div className="mt-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          {info.emoji} {info.label}
        </span>
        <h2 className="mt-2 text-2xl font-bold leading-snug text-[#0B2D4D] break-words">{act.title}</h2>
        <p className="mt-1 text-sm font-semibold text-slate-500">
          📅 {fmtDate(act.date)} · 🕐 {act.time}
        </p>
        <p className="mt-1 text-sm text-slate-500">📍 {act.location}{act.city ? ` · ${act.city}` : ""}</p>
        {cap != null && (
          <p className="mt-2 inline-block rounded-full bg-[#0B2D4D]/5 px-3 py-1 text-sm font-bold text-[#0B2D4D]" data-testid="activity-capacity">
            👥 {count} / {cap} inscritos
          </p>
        )}
      </div>

      <Card className="mt-4 p-4">
        <p className="text-sm leading-relaxed text-slate-600">{act.short_description}</p>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          {act.price ? (
            <div><p className="text-xs font-semibold text-slate-400">Precio</p><p className="font-semibold text-[#0B2D4D]">{act.price}</p></div>
          ) : null}
          {act.capacity ? (
            <div><p className="text-xs font-semibold text-slate-400">Cupos</p><p className="font-semibold text-[#0B2D4D]">{act.capacity}</p></div>
          ) : null}
          {act.organizer_name ? (
            <div><p className="text-xs font-semibold text-slate-400">Organiza</p><p className="font-semibold text-[#0B2D4D]">{act.organizer_name}</p></div>
          ) : null}
        </div>
        {act.requirements ? (
          <div className="mt-3">
            <p className="text-xs font-semibold text-slate-400">Requisitos</p>
            <p className="text-sm text-slate-600">{act.requirements}</p>
          </div>
        ) : null}
      </Card>

      {/* Calendario (Fase 8D) */}
      <div className="mt-4">
        <Button variant="outline" className="w-full" onClick={() => setCalOpen((v) => !v)} data-testid="activity-calendar-btn">
          Agregar a calendario 📅
        </Button>
        {calOpen && (
          <div className="mt-2 grid gap-2" data-testid="activity-calendar-options">
            <a href={cal.google} target="_blank" rel="noopener noreferrer" data-testid="cal-google" className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-bold text-[#0B2D4D] hover:border-[#67D5B5]">📆 Google Calendar</a>
            <a href={cal.outlook} target="_blank" rel="noopener noreferrer" data-testid="cal-outlook" className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-bold text-[#0B2D4D] hover:border-[#67D5B5]">📧 Outlook</a>
            <a href={cal.ics} target="_blank" rel="noopener noreferrer" data-testid="cal-ics" className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-bold text-[#0B2D4D] hover:border-[#67D5B5]">🍎 Apple Calendar / .ics</a>
          </div>
        )}
      </div>

      {/* Inscripción (Fase 8C) */}
      <div className="mt-5">
        {err && (
          <p className="mb-3 rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="activity-join-error">
            {err}
          </p>
        )}

        {part && part.joined ? (
          <Card className="p-4 text-center" data-testid="activity-joined-state">
            <p className="text-lg font-bold text-[#0B2D4D]">Ya estás inscrito 🐾</p>
            {part.dog_name ? <p className="mt-1 text-sm text-slate-500">Con {part.dog_name}</p> : null}
            <Button
              className="mt-3 w-full"
              onClick={() => setChatOpen(true)}
              data-testid="activity-open-chat-btn"
            >
              Abrir chat de la actividad 🐾
            </Button>
            <Button
              variant="outline"
              className="mt-2 w-full"
              onClick={doLeave}
              disabled={busy}
              data-testid="activity-leave-btn"
            >
              {busy ? "Procesando…" : "Cancelar participación"}
            </Button>
          </Card>
        ) : picking ? (
          <Card className="p-4" data-testid="activity-dog-picker">
            <p className="text-sm font-bold text-[#0B2D4D]">Elige a tu peludito</p>
            {dogs === null ? (
              <p className="mt-3 text-sm text-slate-400">Cargando…</p>
            ) : dogs.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500" data-testid="activity-no-dogs">
                Agrega primero a tu peludito en Mis perros para unirte a esta actividad.
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {dogs.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => doJoin(d.id)}
                    disabled={busy}
                    data-testid={`activity-pick-dog-${d.id}`}
                    className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left transition hover:border-[#67D5B5] disabled:opacity-50"
                  >
                    <img src={d.image || "/dog-placeholder.svg"} alt={d.name} className="h-12 w-12 rounded-xl object-cover" />
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold text-[#0B2D4D]">{d.name}</span>
                      <span className="block text-xs text-slate-500">{d.breed}</span>
                    </span>
                    <span className="text-xl">›</span>
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" className="mt-3 w-full" onClick={() => setPicking(false)} data-testid="activity-pick-cancel">
              Cancelar
            </Button>
          </Card>
        ) : (
          <Button
            className="w-full"
            onClick={startJoin}
            disabled={full}
            data-testid="activity-join-btn"
          >
            {full ? "Cupos agotados" : "Unirme con mi peludito 🐾"}
          </Button>
        )}
        {(!part || !part.joined) && !picking && (
          <p className="mt-3 text-center text-sm text-slate-500" data-testid="activity-chat-locked">
            Únete a la actividad para acceder al chat 🐾
          </p>
        )}
      </div>
    </div>
  );
}

export function MyDates({ onOpenConversation }) {
  const [dates, setDates] = useState(null);

  useEffect(() => {
    api.chats.list().then((chats) => {
      const confirmed = (chats || []).filter(
        (c) => c.appointment && c.appointment.status === "Confirmada"
      );
      setDates(confirmed);
    }).catch(() => setDates([]));
  }, []);

  const apptCalendar = (c) => {
    const ap = c.appointment || {};
    const pad = (n) => String(n).padStart(2, "0");
    const title = `Cita PawCita: ${c.myDog?.name || "Tu perro"} + ${c.dog?.name || "peludito"}`;
    const details = `Cita coordinada en PawCita 🐾${ap.notes ? " — " + ap.notes : ""}`;
    const loc = ap.place || "";
    let google, outlook, ics;
    if (ap.date && ap.hour) {
      const start = new Date(`${ap.date}T${ap.hour.length === 5 ? ap.hour : "10:00"}:00`);
      const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
      const fmt = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
      const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
      google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(loc)}`;
      outlook = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(title)}&startdt=${encodeURIComponent(iso(start))}&enddt=${encodeURIComponent(iso(end))}&body=${encodeURIComponent(details)}&location=${encodeURIComponent(loc)}`;
      const icsText = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//PawCita//Citas//ES", "BEGIN:VEVENT", `UID:${c.chatId}@pawcita`, `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`, `SUMMARY:${title}`, `DESCRIPTION:${details}`, `LOCATION:${loc}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
      ics = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsText)}`;
    }
    return { google, outlook, ics };
  };

  if (dates === null) return null;

  return (
    <div className="mt-5" data-testid="my-dates-section">
      <h2 className="flex items-center gap-2 text-lg font-bold text-[#0B2D4D]">📅 Mis citas concertadas</h2>
      {dates.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500" data-testid="my-dates-empty">
          Aún no tienes citas confirmadas con otros peluditos 🐾
        </p>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {dates.map((c) => {
            const ap = c.appointment;
            const cal = apptCalendar(c);
            return (
              <div key={c.chatId} data-testid={`my-date-card-${c.chatId}`} className="rounded-3xl border border-emerald-200 bg-emerald-50/40 p-4">
                <p className="font-display text-lg font-black text-[#0B2D4D]">🐶 {c.myDog?.name} + {c.dog?.name}</p>
                {c.ownerName ? <p className="text-xs text-slate-500">con {c.ownerName}</p> : null}
                <div className="mt-2 space-y-0.5 text-sm text-slate-600">
                  {ap.place ? <p>📍 {ap.place}</p> : null}
                  <p>🕙 {ap.day || ap.date}{ap.hour ? ` · ${ap.hour}` : ""}</p>
                  <p className="font-bold text-emerald-700">✅ Confirmada</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => onOpenConversation && onOpenConversation(c.dog.id)}
                    data-testid={`my-date-open-chat-${c.chatId}`}
                    className="rounded-full bg-[#0B2D4D] px-4 py-2 text-xs font-bold text-white"
                  >
                    Abrir conversación
                  </button>
                  {cal.google && (
                    <a href={cal.google} target="_blank" rel="noopener noreferrer" data-testid={`my-date-cal-google-${c.chatId}`} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-[#0B2D4D]">📆 Google</a>
                  )}
                  {cal.outlook && (
                    <a href={cal.outlook} target="_blank" rel="noopener noreferrer" data-testid={`my-date-cal-outlook-${c.chatId}`} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-[#0B2D4D]">📧 Outlook</a>
                  )}
                  {cal.ics && (
                    <a href={cal.ics} download={`pawcita-cita.ics`} data-testid={`my-date-cal-ics-${c.chatId}`} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-[#0B2D4D]">🍎 .ics</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Activities({ onOpenConversation }) {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setItems(null);
    api.activities
      .list(cat)
      .then(setItems)
      .catch(() => setItems([]));
  }, [cat]);

  const open = (a) => {
    setSelected(a);
    api.activities.detail(a.id).then(setSelected).catch(() => setSelected(a));
  };

  if (selected) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
        <Detail activity={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 pb-28 sm:px-6" data-testid="activities-screen">
      <h1 className="text-2xl font-bold text-[#0B2D4D]">Actividades</h1>
      <p className="mt-1 text-sm text-slate-500">Planes para disfrutar con tu peludito 🐾</p>

      <MyDates onOpenConversation={onOpenConversation} />

      <h2 className="mt-6 flex items-center gap-2 text-lg font-bold text-[#0B2D4D]">🎉 Actividades públicas</h2>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          data-testid="activity-filter-all"
          onClick={() => setCat("")}
          className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-bold ${cat === "" ? "bg-[#0B2D4D] text-white" : "bg-white text-slate-500 border border-slate-200"}`}
        >
          Todas
        </button>
        {ACTIVITY_CATS.map((c) => (
          <button
            key={c.key}
            data-testid={`activity-filter-${c.key}`}
            onClick={() => setCat(c.key)}
            className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-bold ${cat === c.key ? "bg-[#0B2D4D] text-white" : "bg-white text-slate-500 border border-slate-200"}`}
          >
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <p className="mt-8 text-center text-sm text-slate-400">Cargando…</p>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center" data-testid="activities-empty">
          <div className="text-5xl">🐾</div>
          <p className="mt-3 text-sm text-slate-500">
            Pronto encontrarás actividades PawCita para disfrutar con tu peludito 🐾
          </p>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => {
            const info = actCat(a.category);
            return (
              <Card
                key={a.id}
                data-testid={`activity-card-${a.id}`}
                className="cursor-pointer overflow-hidden transition hover:shadow-lg"
                onClick={() => open(a)}
              >
                <div className="flex h-28 w-full items-center justify-center bg-gradient-to-br from-[#67D5B5]/25 to-[#0B2D4D]/10 text-4xl">
                  {a.image ? (
                    <img src={a.image} alt={a.title} className="h-full w-full object-cover" data-testid={`activity-card-image-${a.id}`} />
                  ) : (
                    info.emoji
                  )}
                </div>
                <div className="p-4">
                  <span className="text-xs font-semibold text-emerald-700">{info.emoji} {info.label}</span>
                  <h3 className="mt-1 text-base font-bold leading-snug text-[#0B2D4D] break-words">{a.title}</h3>
                  <p className="mt-1 text-xs font-semibold text-slate-500">📅 {fmtDate(a.date)} · 🕐 {a.time}</p>
                  <p className="mt-0.5 text-xs text-slate-500">📍 {a.city}</p>
                  {a.capacity_num != null && (
                    <p className="mt-1 text-xs font-bold text-[#0B2D4D]" data-testid={`activity-card-capacity-${a.id}`}>
                      👥 {a.participants_count || 0} / {a.capacity_num} inscritos
                    </p>
                  )}
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{a.short_description}</p>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
